import type { Tour } from '@/types/tour';
import { mediaUrl } from '@/core/tour-loader';

// Splash-Screen vor dem Tour-Viewer. Optional — wird nur angezeigt,
// wenn theme.startScreen.enabled === true und die URL kein ?skip=1
// hat.
//
// Klick auf den Button (oder die Hintergrundflaeche) ruft onContinue
// auf; der Caller raeumt dann den Screen ab und mounted den Viewer.

export function shouldShowStartScreen(tour: Tour): boolean {
  if (tour.theme?.startScreen?.enabled !== true) return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('skip') === '1') return false;
  return true;
}

export function mountStartScreen(
  root: HTMLElement,
  tour: Tour,
  onContinue: () => void,
): void {
  const meta = tour.meta;
  const start = tour.theme?.startScreen ?? {};
  const title = (start.title?.trim() || meta.title || 'Tour').trim();
  const subtitle = (start.subtitle?.trim() || meta.location || '').trim();
  const buttonText = start.buttonText?.trim() || 'Tour starten';
  const logoUrl = tour.theme?.logoUrl;
  const bgUrl = start.backgroundUrl;

  const bgStyle = bgUrl
    ? `background-image: linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.6)), url('${escAttr(mediaUrl(bgUrl))}');`
    : '';

  root.innerHTML = `
    <div class="start-screen" role="dialog" aria-labelledby="start-screen-title" style="${bgStyle}">
      <div class="start-screen__inner">
        ${logoUrl
          ? `<img class="start-screen__logo" src="${escAttr(mediaUrl(logoUrl))}" alt="" />`
          : `<span class="start-screen__mark">${esc(initials(title))}</span>`
        }
        <h1 class="start-screen__title" id="start-screen-title">${esc(title)}</h1>
        ${subtitle ? `<p class="start-screen__subtitle">${esc(subtitle)}</p>` : ''}
        <button type="button" class="start-screen__btn" data-role="start">
          ${esc(buttonText)}
        </button>
      </div>
    </div>
  `;

  const start_btn = root.querySelector<HTMLButtonElement>('[data-role="start"]');
  start_btn?.addEventListener('click', () => onContinue());
  // Auch ENTER an dem Button (default) plus Klick irgendwo auf den Screen
  // soll starten — letzteres aber nur, wenn nicht ein Text markiert wird.
  const screen = root.querySelector<HTMLElement>('.start-screen');
  screen?.addEventListener('click', (e) => {
    if (e.target === start_btn || (e.target as HTMLElement).closest('[data-role="start"]')) return;
    if (window.getSelection()?.toString()) return;
    onContinue();
  });
}

function initials(s: string): string {
  const t = s.trim();
  if (!t) return '·';
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    return (words[0].replace(/[^A-Za-z]/g, '').slice(0, 3)
      || words[0].slice(0, 3)).toUpperCase();
  }
  return words.slice(0, 3).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escAttr(s: string): string {
  return esc(s);
}
